import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme, styled } from '@mui/material/styles';
import { get, getUserIdFromToken, getUserRoleFromToken, post } from 'api';
import { PrimaryTab } from 'components/Tabs';
import avatarWhite from 'assets/images/defaultAvatar-white.svg';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import calendar from 'assets/icons/calendar-check-02.svg';
import { ErrorToast, SuccessToast } from 'utils/Toasts';
import EditMatchResultModal from 'components/EditResultModal';
import { Edit } from '@mui/icons-material';
import BracketLoader from '../BracketLoader';

const GroupTable = ({ level, filter }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const successToastRef = useRef();
  const errorToastRef = useRef();
  const token = getUserIdFromToken();
  const { id } = useParams();
  const [activeLevel, setActiveLevel] = useState('super light');
  const [groups, setGroups] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [showTooltip, setShowTooltip] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const isAdmin = getUserRoleFromToken() === 'admin' ? true : false;

  const LightTooltip = styled(({ className, ...props }) => (
    <Tooltip
      {...props}
      open={props.open}
      onOpen={props.onOpen}
      onClose={props.onClose}
      classes={{ popper: className }}
    />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.base.white,
      color: theme.palette.darkGrey[700],
      boxShadow:
        '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
      fontSize: 12,
      fontFamily: 'Inter, sans-serif',
      padding: '8px 12px',
      borderRadius: '8px',
    },
  }));

  const fetchGroups = async () => {
    const response = await get(`/tournaments/tournamentsreg/${id}`);
    const data = await response.json();

    const filtered = data.filter(
      (item) =>
        item.tournament_gender === filter.gender && item.group_name !== null,
    );
    setAllData(filtered);

    const levelsOrder = ['super light', 'light', 'middle', 'power', 'hard'];
    const availableLevels = filtered
      .map((item) => item.tournament_level.toLowerCase())
      .filter((level) => levelsOrder.includes(level));

    if (!availableLevels.includes(activeLevel)) {
      const fallbackLevel =
        availableLevels.length > 0
          ? levelsOrder.find((level) => availableLevels.includes(level))
          : 'super light';

      setActiveLevel(fallbackLevel);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, [id, token, filter.gender]);

  useEffect(() => {
    if (level) {
      setActiveLevel(level.toLowerCase());
    }
  }, [level]);

  useEffect(() => {
    const filteredGroups = allData.filter(
      (item) =>
        item.tournament_level.toLowerCase() === activeLevel &&
        item.group_name !== null,
    );
    setFilteredData(filteredGroups);
  }, [activeLevel, allData]);

  const handleGroupPlaceChange = async (
    newPlace,
    group_name,
    tournament_id,
    tournament_level,
    tournament_gender,
    user_id,
  ) => {
    try {
      // Update the group place in the backend (null kai ištrintas)
      const response = await post(`/groups/update-place`, {
        group_place: newPlace === '' ? null : newPlace,
        group_name,
        tournament_id,
        tournament_level,
        tournament_gender,
        user_id,
      });

      const responseData = await response.json();

      if (response.ok) {
        successToastRef.current.showSuccess(responseData.msg);
        fetchGroups();
      } else {
        errorToastRef.current.showError(responseData.msg);
      }
    } catch (error) {
      console.error('Error updating group place:', error);
    }
  };

  const openEditModal = (match) => {
    setSelectedMatch(match);
    setIsModalOpen(true);
  };

  const closeEditModal = () => {
    setSelectedMatch(null);
    setIsModalOpen(false);
  };

  const handleOpenModal = async (rez, matchKey, playerA, playerB) => {
    try {
      const queryString = new URLSearchParams({
        matchKey,
        tournament_id: playerA.tournament_id,
        tournament_level: playerA.tournament_level,
        tournament_gender: playerA.tournament_gender,
        tournament_stage: 'Grupės',
        playerA_id: playerA.user_id,
        playerB_id: playerB.user_id,
      }).toString();

      const response = await get(`/matches/tournament-match?${queryString}`);
      const matchData = await response.json();

      const isChallenger = matchData.challenger_id === playerA.user_id;
      const challenger = isChallenger ? playerA : playerB;
      const opponent = isChallenger ? playerB : playerA;

      const result = rez?.includes('202') ? null : rez;
      const sets = result?.split(' ') || [];

      let challengerWins = 0;
      let opponentWins = 0;
      sets.forEach((set) => {
        const [c, o] = set.split(':').map(Number);
        if (c > o) challengerWins++;
        if (o > c) opponentWins++;
      });

      const match = {
        matchKey,
        playerA: challenger, // always challenger
        playerB: opponent, // always opponent
        result,
        resultFromA: challenger[matchKey]?.includes('202')
          ? null
          : challenger[matchKey],
        resultFromB: opponent[matchKey]?.includes('202')
          ? null
          : opponent[matchKey],
        tournament_id: challenger.tournament_id,
        tournament_gender: challenger.tournament_gender,
        tournament_level: challenger.tournament_level,
        tournament_stage: 'Grupės',
        winner_id:
          challengerWins > opponentWins
            ? challenger.user_id
            : opponentWins > challengerWins
            ? opponent.user_id
            : '',
        loser_id:
          challengerWins < opponentWins
            ? challenger.user_id
            : opponentWins < challengerWins
            ? opponent.user_id
            : '',
      };

      openEditModal(match);
    } catch (err) {
      console.error('Failed to fetch match data:', err);
      errorToastRef.current.showError('Nepavyko atidaryti rezultato lango');
    }
  };

  const divideByGroups = useCallback(() => {
    const groups = {
      A: [],
      B: [],
      C: [],
      D: [],
      E: [],
      F: [],
      G: [],
      H: [],
      I: [],
      J: [],
      K: [],
      L: [],
      M: [],
      N: [],
      O: [],
      P: [],
      Q: [],
      R: [],
    };

    filteredData.forEach((item) => {
      groups[item.group_name].push(item);
    });

    setGroups(Object.values(groups));
  }, [filteredData]);

  useEffect(() => {
    if (filteredData.length > 0) {
      divideByGroups();
      setLoaded(true);
    }
  }, [filteredData, divideByGroups]);

  const groupSize5 = 5;
  const groupSize6 = 6;
  const groupSize7 = 7;

  const isWinner = (rez) => {
    if (rez === null) {
      return false;
    }
    const sets = rez.split(' ');
    let playerWins = 0;
    let opponentWins = 0;
    sets.forEach((set) => {
      const scores = set.split(':');
      const playerScore = parseInt(scores[0]);
      const opponentScore = parseInt(scores[1]);
      if (playerScore > opponentScore) {
        playerWins++;
      } else if (playerScore < opponentScore) {
        opponentWins++;
      }
    });
    return playerWins > opponentWins;
  };

  const updatedRez = (rez, matchKey, playerA, playerB) => {
    // if (rez === null) return '';

    if (rez && rez.includes('202')) {
      return (
        <LightTooltip
          title={rez}
          placement="top"
          open={
            !isAdmin ? showTooltip[`${playerA.user_id}`] || false : undefined
          }
          onOpen={() => {
            if (!isAdmin) {
              setShowTooltip((prevState) => ({
                ...prevState,
                [`${playerA.user_id}`]: true,
              }));
            }
          }}
          onClose={() => {
            if (!isAdmin) {
              setShowTooltip((prevState) => ({
                ...prevState,
                [`${playerA.user_id}`]: false,
              }));
            }
          }}
        >
          <Box
            component={'img'}
            src={calendar}
            sx={{ cursor: 'pointer' }}
            alt="calendar icon"
            onClick={(e) => {
              e.stopPropagation();

              if (isAdmin) {
                handleOpenModal(rez, matchKey, playerA, playerB);
              } else {
                setShowTooltip((prevState) => ({
                  ...prevState,
                  [`${playerA.user_id}`]: !prevState[`${playerA.user_id}`],
                }));
              }
            }}
          />
        </LightTooltip>
      );
    } else {
      const parts = rez?.split(' ');
      const regex = /\((\d+)\)/g;
      const formatted = parts
        ?.map((part) => part.replace(regex, '<sup>$1</sup>'))
        .join(' ');

      return (
        <Box
          component="span"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <Typography
            variant="body5"
            fontWeight={isWinner(rez) ? 700 : 500}
            color={
              isWinner(rez)
                ? theme.palette.darkGrey[700]
                : theme.palette.darkGrey[600]
            }
            dangerouslySetInnerHTML={{ __html: formatted }}
          />
          {isAdmin && (
            <Edit
              sx={{
                ml: '4px',
                fontSize: '16px',
                color: theme.palette.grey[500],
                cursor: 'pointer',
                '&:hover': {
                  color: theme.palette.primary.main,
                },
              }}
              onClick={() => handleOpenModal(rez, matchKey, playerA, playerB)}
            />
          )}
        </Box>
      );
    }
  };

  const createCells = (i, player, groupPlayers) => {
    const getMatchKey = (i, j) => {
      const [a, b] = [i + 1, j + 1].sort((x, y) => x - y);
      return `M${a}${b}`;
    };

    const {
      points,
      group_name,
      group_place,
      user_id,
      tournament_id,
      tournament_level,
      tournament_gender,
    } = player;

    return (
      <>
        {groupPlayers.map((opponent, index) => {
          const matchKey = getMatchKey(i, index);
          const matchResult = player[matchKey];

          if (index === i) {
            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  width: '126px',
                  justifyContent: 'center',
                  borderLeft: `1px solid ${theme.palette.grey[500]}`,
                  backgroundColor: theme.palette.darkGrey[300],
                  padding: '20px 12px',
                  minHeight: '92px',
                }}
              />
            );
          }

          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                width: '126px',
                boxSizing: 'border-box',
                justifyContent: 'space-evenly',
                padding: '20px 12px',
                minHeight: '92px',
                borderLeft: `1px solid ${theme.palette.grey[500]}`,
                alignItems: 'center',
              }}
            >
              {updatedRez(matchResult, matchKey, player, opponent)}
            </Box>
          );
        })}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            width: '126px',
            boxSizing: 'border-box',
            justifyContent: 'center',
            padding: '20px 16px',
            minHeight: '92px',
            borderLeft: `1px solid ${theme.palette.grey[500]}`,
            alignItems: 'center',
            backgroundColor: theme.palette.darkGrey[100],
          }}
        >
          <Typography
            variant="body4"
            fontWeight={600}
            color={theme.palette.darkGrey[700]}
          >
            {!points ? '0' : points}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            width: '126px',
            borderBox: 'box-sizing',
            justifyContent: 'center',
            padding: '20px 16px',
            minHeight: '92px',
            borderLeft: `1px solid ${theme.palette.grey[500]}`,
            alignItems: 'center',
            backgroundColor: theme.palette.darkGrey[100],
          }}
        >
          {isAdmin ? (
            <input
              type="number"
              value={group_place || ''}
              onChange={(e) =>
                handleGroupPlaceChange(
                  e.target.value,
                  group_name,
                  tournament_id,
                  tournament_level,
                  tournament_gender,
                  user_id,
                )
              }
              style={{
                width: '50px',
                textAlign: 'center',
                fontSize: '14px',
                fontWeight: 600,
                color: theme.palette.darkGrey[700],
                border: `1px solid ${theme.palette.grey[500]}`,
                borderRadius: '4px',
                padding: '4px',
              }}
            />
          ) : (
            <Typography
              variant="body4"
              fontWeight={600}
              color={theme.palette.darkGrey[700]}
            >
              {!group_place ? '-' : group_place}
            </Typography>
          )}
        </Box>
      </>
    );
  };

  if (!loaded) {
    return <BracketLoader />;
  }

  return (
    <Box>
      <SuccessToast ref={successToastRef} />
      <ErrorToast ref={errorToastRef} />
      <EditMatchResultModal
        open={isModalOpen}
        onClose={closeEditModal}
        match={selectedMatch}
        fetchGroups={fetchGroups}
        successToastRef={successToastRef}
        errorToastRef={errorToastRef}
        openedFrom="group"
      />
      <Box
        maxWidth={'700px'}
        marginBottom={'48px'}
        display={{ xs: 'flex', sm: 'inline-flex' }}
        justifyContent={'space-between'}
        border={`1px solid ${theme.palette.darkGrey[300]}`}
        borderRadius={'8px'}
        flexDirection={{ xs: 'column', sm: 'row' }}
      >
        {['super light', 'light', 'middle', 'power', 'hard'].map((level) => {
          const hasLevel = allData.some(
            (item) => item.tournament_level.toLowerCase() === level,
          );

          return (
            hasLevel && (
              <PrimaryTab
                key={level}
                activeTab={activeLevel}
                setActiveTab={setActiveLevel}
                tabName={level}
                pageName={'groupTable'}
              />
            )
          );
        })}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {groups.map((group, index) => {
          const groupNames = [
            'A',
            'B',
            'C',
            'D',
            'E',
            'F',
            'G',
            'H',
            'I',
            'J',
            'K',
            'L',
            'M',
            'N',
            'O',
            'P',
            'Q',
            'R',
          ];
          const groupName = groupNames[index] || '';

          if (group.length === 0) {
            return '';
          }

          const groupSize = group.length;
          const tableWidth = groupSize * 126 + 280 + 126 + 126 + 4;
          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                maxWidth: `${tableWidth}px`,
                overflowX: 'auto',
                marginBottom: '32px',
                paddingBottom: '4px',
                '&::-webkit-scrollbar': {
                  height: '6px',

                  backgroundColor: theme.palette.grey[200],
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.grey[400],
                  borderRadius: '8px',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
                key={index}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      width: '280px',
                      paddingLeft: '16px',
                    }}
                  >
                    <Typography
                      variant="body1"
                      fontWeight={700}
                      color={theme.palette.darkGrey[700]}
                    >
                      {t('group')} {groupName}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'flex-end',
                      width: 'auto',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        width: '126px',
                        justifyContent: 'center',
                        minHeight: '3rem',
                      }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        width: '126px',
                        justifyContent: 'center',
                        minHeight: '3rem',
                      }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        width: '126px',
                        justifyContent: 'center',
                        minHeight: '3rem',
                      }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        width: '126px',
                        justifyContent: 'center',
                        minHeight: '3rem',
                      }}
                    />
                    {group.length < groupSize5 ? (
                      ''
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          width: '126px',

                          justifyContent: 'center',
                          minHeight: '3rem',
                        }}
                      />
                    )}
                    {group.length < groupSize6 ? (
                      ''
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          width: '126px',

                          justifyContent: 'center',
                          minHeight: '3rem',
                        }}
                      />
                    )}
                    {group.length < groupSize7 ? (
                      ''
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          width: '126px',

                          justifyContent: 'center',
                          minHeight: '3rem',
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        width: '126px',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        variant="body4"
                        fontWeight={500}
                        color={theme.palette.darkGrey[700]}
                      >
                        {t('points')}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        width: '126px',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        variant="body4"
                        fontWeight={500}
                        color={theme.palette.darkGrey[700]}
                      >
                        {t('place')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    border: `2px solid ${theme.palette.darkGrey[400]}`,
                    borderRadius: '16px',
                    width: '100%',
                    overflow: 'hidden',
                  }}
                >
                  {group.map((player, i) => {
                    const isLastItem = i === group.length - 1;
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          width: '100%',
                          minHeight: '92px',

                          borderBottom: isLastItem
                            ? 'none'
                            : `1px solid ${theme.palette.grey[500]}`,
                        }}
                        key={player.user_id}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            width: '280px',
                            padding: '20px 16px',
                            minHeight: '92px',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            backgroundColor: theme.palette.darkGrey[100],
                          }}
                        >
                          <Box
                            sx={{
                              width: '52px',
                              height: '52px',
                              flexShrink: 0,
                            }}
                          >
                            <Box
                              component={'img'}
                              src={
                                player.image
                                  ? `https://backend.tenisopartneris.lt/public/images/${player.image}`
                                  : avatarWhite
                              }
                              alt="avatar"
                              sx={{
                                width: '100%',
                                height: '100%',
                                borderRadius: '50%',
                                objectFit: 'cover',
                              }}
                            />
                          </Box>
                          <Box sx={{ marginLeft: '16px', maxWidth: '200px' }}>
                            {' '}
                            {/* Set a maxWidth to control text overflow */}
                            <Box
                              component={'a'}
                              sx={{
                                cursor: 'pointer',
                                textDecoration: 'none',
                                '&:hover': {
                                  textDecoration: 'underline',
                                  textDecorationColor:
                                    theme.palette.darkGrey[700],
                                },
                              }}
                              href={`${window.location.origin}/info/${player.user_id}`}
                            >
                              <Typography
                                variant="body4"
                                fontWeight={600}
                                color={theme.palette.darkGrey[700]}
                                sx={{
                                  whiteSpace: 'normal',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                }}
                              >
                                {player.first_name} {player.last_name}
                              </Typography>
                            </Box>
                            <Box
                              component={'a'}
                              sx={{
                                cursor: 'pointer',
                                textDecoration: 'none',
                                '&:hover': {
                                  textDecoration: 'underline',
                                  textDecorationColor:
                                    theme.palette.darkGrey[700],
                                },
                              }}
                              href={`https://tenisopartneris.lt/info/${player.doubles_partner}`}
                            >
                              <Typography
                                variant="body4"
                                fontWeight={600}
                                color={theme.palette.darkGrey[700]}
                                sx={{
                                  whiteSpace: 'normal', // Allow text to wrap
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 1, // Limit to 1 line
                                  WebkitBoxOrient: 'vertical',
                                }}
                              >
                                {player.partner_first_name}{' '}
                                {player.partner_last_name}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        {createCells(i, player, group)}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default GroupTable;
